import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { useToast } from 'src/hooks/use-toast';
import {
  useLoginMutation,
  useLogoutMutation,
} from 'src/services/api/queries/userQueries';
import { useForgotPasswordMutation } from 'src/services/api/queries/authQueries';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import logo from 'src/assets/images/ahedge_icon_blue_inside.png';
import {
  getRememberedAccount,
  clearRememberedAccount,
} from 'src/lib/rememberedAccount';

const Login = () => {
  const { toast } = useToast();
  const loginMutation = useLoginMutation();
  const logoutMutation = useLogoutMutation();
  const forgotPasswordMutation = useForgotPasswordMutation();

  const [screen, setScreen] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // The last account signed in on this browser, if any — read once on
  // mount (a fresh login re-derives it via setRememberedAccount, so a
  // stale in-memory copy here never matters). When present, the login
  // screen opens on a "welcome back" card instead of the full form; email
  // is seeded from it so handleLoginSubmit needs no other changes.
  const [remembered, setRemembered] = useState(() => getRememberedAccount());
  // Whether the welcome-back card has been clicked to reveal the password
  // field — starts collapsed so returning here reads as "confirm it's you"
  // rather than dropping straight into a password prompt.
  const [passwordRevealed, setPasswordRevealed] = useState(false);

  const [email, setEmail] = useState(() => getRememberedAccount()?.email || '');
  const [password, setPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    logoutMutation.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    try {
      await loginMutation.mutateAsync({ email, password });
    } catch (error) {
      const msg =
        error.message || 'An error occurred during login. Please try again.';
      setApiError(msg);
      toast({ message: msg, variant: 'destructive' });
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    try {
      await forgotPasswordMutation.mutateAsync({ email: forgotEmail });
      setForgotSuccess(true);
    } catch (error) {
      const msg = error.message || 'An error occurred. Please try again.';
      setApiError(msg);
      toast({ message: msg, variant: 'destructive' });
    }
  };

  const goToForgot = () => {
    setScreen('forgot');
    setApiError('');
    setForgotSuccess(false);
    setForgotEmail('');
  };

  const goToLogin = () => {
    setScreen('login');
    setApiError('');
  };

  // "Not you? Use another account" — drops back to the full email+password
  // form and forgets this browser's remembered account entirely, rather
  // than just hiding the card for this visit.
  const handleUseAnotherAccount = () => {
    clearRememberedAccount();
    setRemembered(null);
    setPasswordRevealed(false);
    setEmail('');
    setPassword('');
    setApiError('');
  };

  const rememberedInitials = (() => {
    if (!remembered) return '';
    const first = remembered.firstName?.charAt(0) || '';
    const last = remembered.lastName?.charAt(0) || '';
    return (
      (first + last).toUpperCase() ||
      remembered.email?.charAt(0)?.toUpperCase() ||
      'U'
    );
  })();

  const isLoginLoading = loginMutation.isPending || logoutMutation.isPending;
  const isForgotLoading = forgotPasswordMutation.isPending;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        background:
          'linear-gradient(178deg, #2168B2 0%, #2168B2 35%, #2168B2 70%, #1B5CA0 100%)',
      }}
    >
      {/* Grid overlay */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Glow blob */}
      <div
        className="fixed pointer-events-none"
        style={{
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(32, 104, 178,0.18) 0%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />

      {/* Card */}
      <div
        className="relative z-10 w-full bg-card flex flex-col overflow-hidden"
        style={{ maxWidth: '340px', borderRadius: '16px' }}
      >
        {/* Top accent bar */}
        <div
          style={{
            height: '3px',
            background:
              'linear-gradient(90deg, #2168B2 0%, #2168B2 50%, #2168B2 100%)',
          }}
        />

        {/* Card body */}
        <div className="px-7 pt-8 pb-6">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <img
              src={logo}
              alt="Ahedge logo"
              className="mb-3"
              style={{ width: '48px', height: '48px', objectFit: 'contain' }}
            />
            <div className="text-center">
              <div className="font-bold text-[17px] text-foreground-strong leading-tight">
                AHEDGE
              </div>
              <div className="text-[11px] text-gray-400 mt-0.5">
                Ahedge People Services
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 mb-5" />

          {/* Welcome-back screen — a remembered account on this browser.
              Opens on just the profile card; clicking it reveals the
              password field, skipping re-entry of the email address. */}
          {screen === 'login' && remembered && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setPasswordRevealed(true)}
                aria-expanded={passwordRevealed}
                className="w-full flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 text-left transition-colors hover:border-brand-deep hover:bg-brand-bg-subtle/40 cursor-pointer"
              >
                <div className="w-9 h-9 min-w-[36px] rounded-full bg-gradient-to-br from-[#E9F1FA] to-[#D4E4F7] flex items-center justify-center text-[12px] font-bold text-primary flex-shrink-0">
                  {rememberedInitials}
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[13px] font-semibold text-foreground-strong truncate">
                    {`${remembered.firstName || ''} ${remembered.lastName || ''}`.trim() ||
                      remembered.email}
                  </span>
                  <span className="text-[11px] text-gray-400 truncate">
                    {remembered.email}
                  </span>
                </div>
              </button>

              {passwordRevealed && (
                <form onSubmit={handleLoginSubmit} className="space-y-3">
                  {apiError && (
                    <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                      {apiError}
                    </div>
                  )}

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      required
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoginLoading}
                      className="pl-9 pr-9 h-10 text-sm"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-xs text-brand-deep hover:underline"
                      onClick={goToForgot}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-10 text-[13px] font-semibold border-0 bg-primary hover:bg-brand-deep rounded-lg"
                    disabled={isLoginLoading}
                  >
                    {isLoginLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Signing in...
                      </span>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              )}

              <button
                type="button"
                onClick={handleUseAnotherAccount}
                className="block mx-auto text-xs text-gray-400 hover:text-gray-600 hover:underline"
              >
                Not you? Use another account
              </button>
            </div>
          )}

          {/* Login screen — the full email+password form, used when no
              account is remembered on this browser (or "Use another
              account" was chosen). */}
          {screen === 'login' && !remembered && (
            <form onSubmit={handleLoginSubmit} className="space-y-3">
              {apiError && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {apiError}
                </div>
              )}

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Email address"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoginLoading}
                  className="pl-9 h-10 text-sm"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoginLoading}
                  className="pl-9 pr-9 h-10 text-sm"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-xs text-brand-deep hover:underline"
                  onClick={goToForgot}
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-10 text-[13px] font-semibold border-0 bg-primary hover:bg-brand-deep rounded-lg"
                disabled={isLoginLoading}
              >
                {isLoginLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          )}

          {/* Forgot password form */}
          {screen === 'forgot' && !forgotSuccess && (
            <div className="space-y-4">
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 -mt-1 mb-1"
                onClick={goToLogin}
              >
                <ArrowLeft className="w-3 h-3" /> Back to Sign In
              </button>

              <div className="flex flex-col items-center gap-2 mb-1">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#F0FAFB',
                  }}
                >
                  <Lock className="w-5 h-5 text-brand-deep" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-[14px] text-foreground-strong">
                    Reset Password
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    Enter your email to receive a reset link
                  </div>
                </div>
              </div>

              {apiError && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {apiError}
                </div>
              )}

              <form onSubmit={handleForgotSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="Email address"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    disabled={isForgotLoading}
                    className="pl-9 h-10 text-sm"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-10 text-[13px] font-semibold border-0 bg-primary hover:bg-brand-deep rounded-lg"
                  disabled={isForgotLoading}
                >
                  {isForgotLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Sending...
                    </span>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Forgot password success */}
          {screen === 'forgot' && forgotSuccess && (
            <div className="flex flex-col items-center gap-4 py-2">
              <div
                className="flex items-center justify-center"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#ECFDF5',
                }}
              >
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-[14px] text-foreground-strong">
                  Check your inbox
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  We sent a reset link to
                </div>
                <div className="text-[12px] font-medium text-gray-700 mt-0.5">
                  {forgotEmail}
                </div>
              </div>
              <button
                type="button"
                className="text-xs text-brand-deep hover:underline mt-1"
                onClick={goToLogin}
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>

        {/* Card footer */}
        <div className="border-t border-gray-100 dark:border-border px-7 py-3 bg-muted text-center">
          <span className="text-[10.5px] text-muted-foreground">
            Need help?{' '}
            <a
              href="mailto:support@ahedge.in"
              className="text-brand-deep hover:underline"
            >
              Contact support
            </a>
          </span>
        </div>
      </div>

      {/* Below card */}
      <div className="relative z-10 mt-5 text-center text-[10.5px] text-white/40">
        Â© 2026 Ahedge People Services Â· Privacy Policy
      </div>
    </div>
  );
};

export default Login;
